class AriaListbox extends HTMLElement {
  static observedAttributes = ["active"];

  constructor() {
    super();

    this.handleKeyDown = (e) => {
      if (!this.active) return;

      switch (e.code) {
        case "ArrowUp":
          e.preventDefault();
          this._previous();
          break;
        case "ArrowDown":
          e.preventDefault();
          this._next();
          break;
        case "Home":
          e.preventDefault();
          this._first();
          break;
        case "End":
          e.preventDefault();
          this._last();
          break;
        default:
          break;
      }
    };

    this.addEventListener("click", (e) => {
      const option = e.target.closest(
        '[role="option"]:not([aria-disabled="true"])',
      );
      if (!option) return;

      this.options.forEach((el) => {
        el.tabIndex = -1;
      });
      this.selected.forEach((el) => el.setAttribute("aria-selected", "false"));
      option.setAttribute("aria-selected", "true");
      option.removeAttribute("tabindex");
      this._setInputValue(option.dataset.value);
      option.dispatchEvent(
        new CustomEvent("aria-listbox:change", {
          bubbles: true,
          detail: { value: option.dataset.value },
        }),
      );
    });
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "active" && oldValue === null && newValue !== null) {
      this._focus(this.selected[0]);
    }
  }

  connectedCallback() {
    document.addEventListener("keydown", this.handleKeyDown);
    this.options.forEach((el) => {
      if (el.getAttribute("aria-selected") === "true") {
        el.removeAttribute("tabindex");
      } else {
        el.tabIndex = -1;
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  get active() {
    return this.hasAttribute("active");
  }

  get options() {
    return [
      ...this.querySelectorAll('[role="option"]:not([aria-disabled="true"])'),
    ];
  }

  get selected() {
    return [...this.querySelectorAll('[role="option"][aria-selected="true"]')];
  }

  get focused() {
    return [...this.querySelectorAll('[role="option"][data-focused]')];
  }

  _setInputValue(value) {
    const input = this.querySelector("[data-input]");

    if (!input) return;

    input.value = value;
  }

  _first() {
    this._focus(this.options[0]);
  }

  _last() {
    const options = this.options;
    this._focus(options[options.length - 1]);
  }

  _next() {
    const options = this.options;
    const currentIndex = options.findIndex((el) =>
      el.hasAttribute("data-focused"),
    );
    const newIndex = Math.min(currentIndex + 1, options.length - 1);
    if (newIndex === currentIndex) return;

    this._focus(options[newIndex]);
  }

  _previous() {
    const options = this.options;
    const currentIndex = options.findIndex((el) =>
      el.hasAttribute("data-focused"),
    );
    const newIndex = Math.max(currentIndex - 1, 0);
    if (newIndex === currentIndex) return;

    this._focus(options[newIndex]);
  }

  _focus(el) {
    this.options.forEach((el) => {
      el.tabIndex = -1;
      el.removeAttribute("data-focused");
    });
    if (!el) return;
    el.removeAttribute("tabindex");
    el.setAttribute("data-focused", "");
    el.focus();
  }
}

if (!customElements.get("aria-listbox")) {
  customElements.define("aria-listbox", AriaListbox);
}
